import { z } from "zod";

/**
 * The name of one point in the application where an effect resolves: `<domain>.<kind>.<point>`.
 *
 * A rule declares the location its effect belongs to, and a location that a rule does not name never
 * evaluates that rule. Without it a rule can only say *what* happens (`RuleEffect.type`) and *when*
 * (`RuleCondition[]`), so every enabled `block` rule fires at every gated point — the over-blocking this
 * vocabulary exists to remove.
 *
 * All three segments are `snake_case`. The first segment names the gated *document* (`sales_order`,
 * `purchase_order` — decision recorded on SC-3027): it is a plain namespace label, not a rule domain, and a
 * document's rules still evaluate under whatever rule domain its consumer names (`regulatory`, for both
 * shipped status-transition namespaces). Where a document coincides with a rule domain, as `sales_order`
 * does, reuse that constant's spelling (`ruleDomainConstants.salesOrder === "sales_order"`,
 * `src/shared/constants/rule-domain.constants.ts:21`) rather than introducing a second one. The point
 * segment is snake for the same reason every other stored rules-engine vocabulary value is
 * (`apply_template`, `sales_order`) — one token must not mix two casings.
 *
 * Branded so a bare `string` cannot be passed where a token is required. The brand is compile-time only, so
 * every function here re-validates rather than trusting it; the point of the brand is that the *only*
 * ordinary way to obtain one is `buildEffectLocationToken` or `effectLocationTokenSchema`, both of which
 * validate.
 *
 * This module lives under `~/shared` and not under `~/server` because `regulatoryEffectConfigSchema`
 * (`src/shared/types/RegulatoryEffect.ts`) imports it, and a `~/shared` module must not import `~/server/*`.
 * Nothing here is server-only: it is a regex and two zod schemas.
 */
export type EffectLocationToken = string & { readonly __brand: "EffectLocationToken" };

/**
 * The `<domain>.<kind>` prefix a registry owns — the first two segments of a token.
 *
 * A namespace is what separates "another subsystem's location, which this one cannot judge" from "a point
 * this namespace is supposed to know and does not". The first is a scope and is skipped; the second is a
 * defect and throws. Registries are keyed on it: a registry owning `sales_order.status_transition` refuses
 * to register an `inventory.*` location.
 */
export type EffectLocationNamespace = string & { readonly __brand: "EffectLocationNamespace" };

/**
 * One `snake_case` segment: lower-case alphanumeric words joined by single underscores, never leading with a
 * digit or an underscore, never trailing one, never doubled.
 *
 * So `Sales_Order.x.y` (upper case), `sales-order.x.y` (kebab), `1a.b.c` (leading digit), `a..c` (empty
 * segment), `a.b` (two segments) and `a.b.c.d` (four) are all rejected.
 */
const EFFECT_LOCATION_SEGMENT = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;

/**
 * Split a candidate token into its three segments, or `null` when it is not a token.
 *
 * The single implementation of the token grammar: both `effectLocationTokenSchema` and
 * `describeEffectLocationToken` are written on top of it, so the validating and the destructuring halves
 * cannot drift apart. The `undefined` comparisons are not impossible-state guards — `noUncheckedIndexedAccess`
 * is on (`tsconfig.json`), so a destructured element is `string | undefined` until something narrows it, and
 * this check is what narrows it as well as what enforces the segment count.
 */
const _describeCandidateToken = (value: string): { domain: string; kind: string; point: string } | null => {
  const [domain, kind, point, ...rest] = value.split(".");
  if (domain === undefined || kind === undefined || point === undefined || rest.length > 0) {
    return null;
  }
  if (![domain, kind, point].every(segment => EFFECT_LOCATION_SEGMENT.test(segment))) {
    return null;
  }
  return { domain, kind, point };
};

const _isNamespaceShaped = (value: string): boolean => {
  const segments = value.split(".");
  return segments.length === 2 && segments.every(segment => EFFECT_LOCATION_SEGMENT.test(segment));
};

/**
 * Parses a stored string into an `EffectLocationToken`, rejecting anything that is not exactly three
 * `snake_case` segments.
 *
 * Used by `regulatoryEffectConfigSchema` to read `RuleEffect.config`, which is a free `Json` column
 * (`prisma/schema.prisma:6681`) with no shape constraint, so a malformed or absent location is reachable from
 * data rather than an impossible state. The refinement is a type predicate so the parsed value carries the
 * brand without a cast.
 */
export const effectLocationTokenSchema = z
  .string()
  .refine((value): value is EffectLocationToken => _describeCandidateToken(value) !== null, {
    message: "An effect location token is `<domain>.<kind>.<point>`, all snake_case",
  });

const _effectLocationNamespaceSchema = z.string().refine((value): value is EffectLocationNamespace => _isNamespaceShaped(value), {
  message: "An effect location namespace is `<domain>.<kind>`, both snake_case",
});

/**
 * Build the `<domain>.<kind>` prefix a registry owns.
 *
 * Throws when either segment is not `snake_case`. The first segment is the gated document's label
 * (SC-3027): a caller whose document coincides with a rule domain passes that constant
 * (`ruleDomainConstants.salesOrder`) so the two vocabularies cannot drift apart, and a caller whose
 * document is not a rule domain (`purchase_order`) passes its plain label.
 */
export const buildEffectLocationNamespace = (domain: string, kind: string): EffectLocationNamespace =>
  _effectLocationNamespaceSchema.parse(`${domain}.${kind}`);

/**
 * Build the token naming one point inside a namespace.
 *
 * Throws when `point` is not `snake_case`. The namespace argument is already branded, so the only way to
 * reach an invalid token is an invalid point.
 */
export const buildEffectLocationToken = (namespace: EffectLocationNamespace, point: string): EffectLocationToken =>
  effectLocationTokenSchema.parse(`${namespace}.${point}`);

/**
 * Split a token into its three segments.
 *
 * Throws on a token that does not parse. That is reachable despite the brand — the brand is erased at
 * runtime, so a value cast into the type, or one read from `RuleEffect.config` and asserted rather than
 * parsed, arrives here malformed. Failing loudly is the same fail-closed choice the rest of this concept
 * makes: a location that cannot be read is not a location that matched nothing.
 */
export const describeEffectLocationToken = (token: EffectLocationToken): { domain: string; kind: string; point: string } => {
  const described = _describeCandidateToken(token);
  if (described === null) {
    throw new Error(`"${token}" is not an effect location token; expected <domain>.<kind>.<point>, all snake_case`);
  }
  return described;
};

/**
 * The namespace a token belongs to.
 *
 * This is the function that draws the routing line: two tokens in different namespaces belong to different
 * subsystems, and neither one's registry is entitled to judge the other's points, so a rule scoped to another
 * namespace is skipped. Within one namespace the registry is claiming to know every point, so a point it does
 * not know is a defect and throws. Without a namespace there is no way to tell those two cases apart, and the
 * second would have to be silently skipped — turning a `Block` an org configured into one that never fires.
 */
export const effectLocationNamespaceOf = (token: EffectLocationToken): EffectLocationNamespace => {
  const { domain, kind } = describeEffectLocationToken(token);
  return buildEffectLocationNamespace(domain, kind);
};
